import {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { State as RootState } from "../state";
import { VehicleState } from "./vehicleState";
import Response from "../../models/response";
import { SearchVehiclesQueryContext } from "../../api/dto/searchVehiclesQueryContext";
import Vehicle from "../../models/vehicle";
import VehicleApi from "@/api/vehicle";

type VehicleContext = ActionContext<VehicleState, RootState>;

const state: VehicleState = {
  detailedVehicle: null,
  isLoading: false,
  total: 0,
  vehicles: [],
};

const getters: GetterTree<VehicleState, RootState> = {
  isLoading(state: VehicleState): boolean {
    return state.isLoading;
  },

  getTotal(state: VehicleState): number {
    return state.total;
  },

  getVehicles(state: VehicleState): Vehicle[] {
    return state.vehicles;
  },

  getDetailedVehicle(state: VehicleState): Vehicle | null {
    return state.detailedVehicle;
  },

  getVehicleByVin:
    (state: VehicleState) =>
    (vin: string): Vehicle | undefined => {
      return state.vehicles.find((x) => x.vin === vin);
    },
};

const actions: ActionTree<VehicleState, RootState> = {
  fetchAll(
    context: VehicleContext,
    queryContext: SearchVehiclesQueryContext
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      context.commit("fetchAllRequest");

      VehicleApi.search(queryContext)
        .then((resp: Response) => {
          const pagination = resp.payload.pagination;
          const data = resp.payload.vehicles.map((d: any) =>
            Vehicle.fromJSON(d)
          );
          context.commit("fetchAllSuccess", {
            vehicles: data,
            pagination: pagination,
          });
          resolve(resp);
        })
        .catch((resp: Response) => {
          context.commit("fetchAllFailure");
          reject(resp);
        });
    });
  },

  fetchByVin(context: VehicleContext, vin: string): Promise<Response> {
    return new Promise((resolve, reject) => {
      context.commit("fetchByVinRequest");

      VehicleApi.getByVin(vin)
        .then((resp: Response) => {
          const data = Vehicle.fromJSON(resp.payload.vehicle);

          context.commit("fetchByVinSuccess", {
            vehicle: data,
          });
          resolve(resp);
        })
        .catch((resp: Response) => {
          context.commit("fetchByVinFailure");
          reject(resp);
        });
    });
  },
};

const mutations: MutationTree<VehicleState> = {
  fetchAllRequest(_state: VehicleState) {
    state.isLoading = true;
  },

  fetchAllSuccess(state: VehicleState, { vehicles, pagination }) {
    state.isLoading = false;
    state.vehicles = vehicles;
    state.total = pagination.total;
  },

  fetchAllFailure(_state: VehicleState) {
    state.isLoading = false;
  },

  fetchByVinRequest(_state: VehicleState) {
    state.detailedVehicle = null;
    state.isLoading = true;
  },

  fetchByVinSuccess(state: VehicleState, { vehicle }) {
    state.isLoading = false;
    state.detailedVehicle = vehicle;
  },

  fetchByVinFailure(_state: VehicleState) {
    state.isLoading = false;
  },
};

export const vehicle: Module<VehicleState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
